/**
 * Home — the analyst's morning briefing.
 *
 * Signal-first command centre, not a mirror of the nav. Everything on the page
 * is backed by a live endpoint: dataset coverage (/v1/stats/coverage), the
 * freshest spin-out signals (radar), and the most-connected firms (network
 * browse). No placeholder CRM / reminders — those return when their backends
 * exist.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CompanyLogo, ConfidencePill, EntityLink } from '@/components/ui';
import { queryKeys } from '@/services/api/queryClient';
import { useUser } from '@/store/authStore';
import { useIsMobile } from '@/hooks';
import { fetchSpinoutCandidates } from '@/features/radar/api/spinoutApi';
import { ARCHETYPE_SHORT } from '@/features/radar/archetypeMeta';
import { fetchBrowse } from '@/features/network/api/hubApi';
import { fetchCoverageStats } from './api/homeApi';
import styles from './HomePage.module.css';

// ---------------------------------------------------------------------------
// Small formatting helpers
// ---------------------------------------------------------------------------

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function relativeTime(iso?: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

const roleLabel = (role?: string | null): string =>
  role ? role.replace(/_/g, ' ') : 'viewer';

// ---------------------------------------------------------------------------
// Coverage strip
// ---------------------------------------------------------------------------

function CoverageStrip() {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.home.coverage(),
    queryFn: fetchCoverageStats,
  });

  const items: Array<{ label: string; value: number; sub?: string }> = data
    ? [
        { label: 'Companies', value: data.companies, sub: `${data.tracked_companies.toLocaleString()} tracked` },
        { label: 'People', value: data.people },
        { label: 'Relationships', value: data.relationships },
        { label: 'Jurisdictions', value: data.jurisdictions },
        { label: 'Regulatory regimes', value: data.regimes },
        { label: 'Active signals', value: data.signals_active },
      ]
    : [];

  return (
    <section className={styles.coverage} aria-label="Dataset coverage">
      <div className={styles.coverageHead}>
        <h2 className={styles.sectionLabel}>Coverage</h2>
        <span className={styles.coverageRefresh}>
          {isLoading
            ? 'Loading…'
            : isError
              ? 'Unavailable'
              : `Refreshed ${relativeTime(data?.updated_at)}`}
        </span>
      </div>
      <div className={styles.coverageGrid}>
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={styles.coverageItem}>
                <span className={`${styles.coverageValue} ${styles.skeleton}`}>&nbsp;</span>
                <span className={styles.coverageItemLabel}>&nbsp;</span>
              </div>
            ))
          : items.map((it) => (
              <div key={it.label} className={styles.coverageItem}>
                <span className={styles.coverageValue}>{it.value.toLocaleString()}</span>
                <span className={styles.coverageItemLabel}>{it.label}</span>
                {it.sub && <span className={styles.coverageItemSub}>{it.sub}</span>}
              </div>
            ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Signals (Radar) — the hero
// ---------------------------------------------------------------------------

function SignalsPanel() {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.home.signals(),
    queryFn: () =>
      fetchSpinoutCandidates({ sort_by: 'detected_at', sort_order: 'desc', limit: 6 }),
  });
  const coverage = useQuery({
    queryKey: queryKeys.home.coverage(),
    queryFn: fetchCoverageStats,
  });

  const rows = data?.results ?? [];

  return (
    <section className={styles.signals} aria-labelledby="signals-label">
      <div className={styles.panelHead}>
        <h2 id="signals-label" className={styles.panelTitle}>
          <span className={styles.signalDot} aria-hidden />
          Signals
        </h2>
        <Link to="/radar" className={styles.viewAll}>
          View all →
        </Link>
      </div>

      <ul className={styles.signalList}>
        {isLoading && (
          <li className={styles.signalEmpty}>Loading the latest detections…</li>
        )}
        {isError && (
          <li className={styles.signalEmpty}>Couldn’t load signals right now.</li>
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <li className={styles.signalEmpty}>No spin-out signals on record yet.</li>
        )}
        {rows.map((c) => (
          <li key={c.id} className={styles.signalItem}>
            <div className={styles.signalTop}>
              <span className={styles.archetypeTag}>{ARCHETYPE_SHORT[c.archetype] ?? c.archetype}</span>
              <ConfidencePill value={c.confidence} compact />
            </div>
            <div className={styles.signalName}>
              <CompanyLogo
                logoUrl={c.new_company?.logo_url}
                domain={c.new_company?.domain}
                name={c.new_company?.name ?? ''}
                size={20}
              />
              <EntityLink
                kind="spinout"
                id={c.id}
                name={c.new_company?.name ?? 'New entity'}
                // new_company.name is already backend-normalized (spinout
                // repository COALESCEs to display_name) — pass it as
                // displayName too so EntityLink uses it verbatim instead of
                // re-running client-side normalizeCompanyName on top of it.
                displayName={c.new_company?.name}
              />
            </div>
            <div className={styles.signalMeta}>
              {c.parent?.name ? (
                <>
                  <span className={styles.signalParent}>
                    <CompanyLogo
                      logoUrl={c.parent?.logo_url}
                      domain={c.parent?.domain}
                      name={c.parent?.name ?? ''}
                      size={14}
                    />
                    {c.parent.name}
                  </span>
                  {' · '}
                </>
              ) : null}
              detected {relativeTime(c.detected_at)}
            </div>
          </li>
        ))}
      </ul>

      {!isLoading && !isError && (
        <div className={styles.signalFooter}>
          {coverage.data
            ? `${coverage.data.signals_active.toLocaleString()} active signals · ${rows.length} most recent shown`
            : `${rows.length} most recent shown`}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Jump-to rail — global search + quick links + access line
// ---------------------------------------------------------------------------

function JumpRail() {
  const { user, role } = useUser();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    // Companies search owns the `q` param; cast mirrors the codebase's
    // search-reducer pattern (TanStack's narrow inference rejects partials).
    navigate({ to: '/database/companies', search: (q ? { q } : {}) as never });
  };

  return (
    <aside className={styles.rail} aria-label="Quick actions">
      <div className={styles.railBlock}>
        <h2 className={styles.sectionLabel}>Jump to</h2>
        <form className={styles.searchForm} onSubmit={onSubmit} role="search">
          <svg
            className={styles.searchIcon}
            width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" aria-hidden
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search company or person…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the database"
          />
        </form>
        <div className={styles.quickLinks}>
          <Link to="/radar" className={styles.quickLink}>Radar</Link>
          <Link to="/network" className={styles.quickLink}>Network</Link>
          <Link to="/database/companies" className={styles.quickLink}>Companies</Link>
          <Link to="/database/people" className={styles.quickLink}>People</Link>
        </div>
      </div>

      <div className={styles.railBlock}>
        <h2 className={styles.sectionLabel}>Your access</h2>
        <div className={styles.accessName}>
          {user?.first_name ? `${user.first_name} ${user.last_name ?? ''}`.trim() : user?.email}
        </div>
        <div className={styles.accessRole}>{roleLabel(role)}</div>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Most-connected firms (Network)
// ---------------------------------------------------------------------------

function FirmsPanel() {
  const isMobile = useIsMobile();
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.home.firms(),
    queryFn: () => fetchBrowse(8),
  });

  const firms = useMemo(() => data?.firms ?? [], [data]);
  const maxStrength = useMemo(
    () => firms.reduce((m, f) => Math.max(m, f.total_strength), 0) || 1,
    [firms],
  );

  return (
    <section className={styles.firms} aria-labelledby="firms-label">
      <div className={styles.panelHead}>
        <h2 id="firms-label" className={styles.panelTitle}>Most-connected firms</h2>
        <Link to="/network" className={styles.viewAll}>View all →</Link>
      </div>

      <div className={styles.firmTableWrap}>
        {isLoading && <div className={styles.firmEmpty}>Loading the network…</div>}
        {isError && <div className={styles.firmEmpty}>Couldn’t load the network right now.</div>}
        {!isLoading && !isError && firms.length === 0 && (
          <div className={styles.firmEmpty}>No firm ties materialised yet.</div>
        )}
        {firms.length > 0 && (
          isMobile ? (
            <div className={styles.firmCardList}>
              {firms.map((f, i) => (
                <Link
                  key={f.id}
                  to="/network"
                  search={{ focus: `firm:${f.id}` } as never}
                  className={styles.firmCard}
                >
                  <div className={styles.firmCardTop}>
                    <span className={styles.firmCardRank}>{i + 1}</span>
                    <div className={styles.firmCardName}>
                      <span className={styles.firmCardTitle}>{f.name}</span>
                      {f.industry && (
                        <span className={styles.firmCardIndustry}>{f.industry}</span>
                      )}
                    </div>
                    <span className={styles.firmCardTies}>{f.tie_count.toLocaleString()} ties</span>
                  </div>
                  <div className={styles.strengthBar} aria-hidden>
                    <span
                      className={styles.strengthFill}
                      style={{ width: `${Math.round((f.total_strength / maxStrength) * 100)}%` }}
                    />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <table className={styles.firmTable}>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Firm</th>
                  <th scope="col" className={styles.firmStrengthCol}>Connection strength</th>
                  <th scope="col" className={styles.firmNumCol}>Ties</th>
                </tr>
              </thead>
              <tbody>
                {firms.map((f, i) => (
                  <tr key={f.id}>
                    <td className={styles.firmRank}>{i + 1}</td>
                    <td>
                      <Link
                        to="/network"
                        search={{ focus: `firm:${f.id}` } as never}
                        className={styles.firmLink}
                      >
                        {f.name}
                      </Link>
                      {f.industry && <span className={styles.firmIndustry}>{f.industry}</span>}
                    </td>
                    <td>
                      <div className={styles.strengthBar} aria-hidden>
                        <span
                          className={styles.strengthFill}
                          style={{ width: `${Math.round((f.total_strength / maxStrength) * 100)}%` }}
                        />
                      </div>
                    </td>
                    <td className={styles.firmTies}>{f.tie_count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function HomePage() {
  const { user } = useUser();
  const name = user?.first_name?.trim();

  return (
    <div className="app-page">
      <header className="app-page__header">
        <div className="app-page__header-left">
          <h1 className="app-page__title">
            {greeting()}{name ? `, ${name}` : ''}.
          </h1>
          <span className="app-page__meta">Private capital intelligence</span>
        </div>
      </header>

      <main
        className={`app-page__content app-page__content--constrained ${styles.briefing}`}
        role="main"
        aria-label="Briefing"
      >
        <CoverageStrip />
        <div className={styles.split}>
          <SignalsPanel />
          <JumpRail />
        </div>
        <FirmsPanel />
      </main>
    </div>
  );
}
